import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { FaqSection, LinkClusterSection } from "@/app/components/seo-support";
import { siteHeaderActions, siteNavItems } from "@/app/content";
import { ROUTE_FAQS } from "@/app/seo-content";
import { HomepageArchitecture } from "@/design-system/patterns/homepage-architecture";

const quickFrames = [
  {
    title: "Protocol = 真相源",
    body:
      "所有 Hotline 共享同一份 contracts 定义。调用外形、token 模型、状态机、TransportAdapter 抽象都从这里生长出来。",
    accent: "#A3E635",
  },
  {
    title: "Client = 本地执行面",
    body:
      "Caller Controller 和 Responder Controller 都跑在用户自己的 runtime 里。你可以只跑一边，也可以同机双边联调。",
    accent: "#3B82F6",
  },
  {
    title: "Platform / Selfhost = 控制面",
    body:
      "Catalog、token、delivery-meta、metrics 走控制面；公网 Platform 与自托管 Selfhost 只是同一控制面服务的两种部署形态。",
    accent: "#8B5CF6",
  },
];

const architectureFaqs = ROUTE_FAQS["/docs/protocol-architecture/"] ?? [];

export default function ProtocolArchitecturePage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <ContentHero
        eyebrow="DOCS · PROTOCOL ARCHITECTURE"
        eyebrowColor="#A3E635"
        titleTop="协议架构"
        titleBottom="控制面与数据面"
        bottomColor="#A3E635"
        lead="这页把首页里偏工程层的 FOR ENGINEERS 内容单独拉出来，集中解释一根 Hotline 是如何被发起、被路由、被交付，以及 Platform / Selfhost 为什么不在业务数据面里 hop。"
        meta={["Protocol", "Control plane", "Data plane", "Selfhost"]}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Docs", href: "/docs/" },
          { label: "Protocol Architecture" },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <section className="grid gap-5 md:grid-cols-3">
          {quickFrames.map((frame) => (
            <article
              key={frame.title}
              className="border-[6px] border-black bg-white/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]"
            >
              <span
                className="inline-flex h-4 w-16 border-2 border-black"
                style={{ backgroundColor: frame.accent }}
                aria-hidden
              />
              <h2 className="mt-4 text-xl font-black uppercase text-black">{frame.title}</h2>
              <p className="mt-3 text-sm font-bold leading-7 text-zinc-800">{frame.body}</p>
            </article>
          ))}
        </section>

        <aside className="mt-12 border-[6px] border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#A3E635]">FIRST READ</p>
          <h2 className="mt-3 text-2xl font-black uppercase sm:text-3xl">先记住一句话</h2>
          <p className="mt-4 text-sm font-bold leading-7 text-zinc-300 sm:text-base">
            CALL ANYTHING 的核心不是“平台代你跑业务流量”，而是“协议把能力发现、身份、结算和可观测标准化；业务输入输出仍在
            Caller / Responder 之间按数据面直连传递”。
          </p>
        </aside>

        <HomepageArchitecture />

        <FaqSection
          eyebrow="PAGE FAQ"
          title="关于协议架构的 3 个常见问题"
          intro="这页优先回答工程团队第一次看到 CALL ANYTHING 架构图时最容易卡住的点。"
          items={architectureFaqs}
          accent="#A3E635"
        />

        <LinkClusterSection
          eyebrow="NEXT"
          title="继续往哪看"
          intro="如果你已经知道控制面和数据面怎么分，再顺着这些稳定入口继续就够了。"
          accent="#A3E635"
          links={[
            {
              href: "/docs/protocol-examples/",
              title: "协议最小示例",
              summary: "先记 request、result_package、error 和 registration payload 的 shape。",
              meta: "Request · result_package · error",
            },
            {
              href: "/glossary/",
              title: "Glossary 术语表",
              summary: "把 Hotline、Caller、Responder、Template Bundle 等边界一次看清。",
              meta: "Hotline · Caller · Responder",
            },
            {
              href: "/docs/quick-start-caller/",
              title: "Caller Quick Start",
              summary: "想把这张架构图映射到真实命令和一次真实返回，就从本地 Caller 闭环开始。",
              meta: "Quick Start · local mode",
            },
            {
              href: "/compare/hotline-vs-mcp/",
              title: "Hotline vs MCP",
              summary: "如果你要解释它为什么不是 MCP Server 的重命名，这页最直接。",
              meta: "Compare · positioning",
            },
          ]}
        />
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
